import React, { useEffect, useState } from "react";
import FormContainer from "../../components/form-container";
import {
  ButtonWithAction,
  Cluster,
  FormInput,
  FormSelect,
  Loading,
  Alert,
} from "@thecb/components/";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFunction,
  getErrorDictionary,
  getSelectOptionsWithPlaceholder,
} from "../../util";
import {
  ClientResponse,
  ClientPostRequestBody,
  ClientPutRequestBody,
  ErrorWithMessage,
  FormType,
  ReduxFreeformActions,
  ReduxStoreRoot,
} from "../../types";
import { useClearFormOnUnmount, useSetFormFieldsState } from "../../hooks";
import { replacePOSAlerts } from "../../state/root";
import { AlertVariants } from "../../types/Alert";
import {
  clientsFormMapDispatchToProps,
  clientsFormMapStateToProps,
  clientsFormReducer,
} from "./ClientsForm.state";
import { ALERTS } from "../../util/constants";
import { RequestMethod } from "../../types/api/RequestMethod";

interface Props {
  formActions: ReduxFreeformActions;
}

const ClientsForm = ({ formActions }: Props) => {
  useClearFormOnUnmount(formActions.form);
  const dispatch = useDispatch();
  const [formType, setFormType] = useState<FormType>(FormType.READ_ONLY);
  const { clientsForm } = useSelector(
    (state: ReduxStoreRoot) => state.posConfigTool.clients.forms
  );
  const { posApiEndpoint } = useSelector((state: ReduxStoreRoot) => state.config);
  const {
    client: {
      value: { brand: selectedBrand },
    },
  } = useSelector((state: ReduxStoreRoot) => state.currentUser);

  const {
    isSuccess: clientExistsInPOS,
    isFetching: isFetchingGET,
    error: getClientError,
  } = useQuery<ClientResponse, ErrorWithMessage>({
    queryKey: [`GET -> /clients/${selectedBrand}`],
    queryFn: () => fetchFunction(`${posApiEndpoint}/clients/${selectedBrand}`),
  });

  const clientDoesNotExistInPOS =
    getClientError instanceof Error &&
    getClientError.message === ALERTS.ERRORS.CLIENT_NOT_FOUND_ERROR;

  const { data: editClientData, isFetching: isFetchingEdit } = useQuery<
    ClientResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /clients/${selectedBrand}/edit`],
    queryFn: () => fetchFunction(`${posApiEndpoint}/clients/${selectedBrand}/edit`),
    enabled: clientExistsInPOS,
  });

  const { data: createClientData, isFetching: isFetchingCreate } = useQuery<
    ClientResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /clients/${selectedBrand}/new`],
    queryFn: () =>
      fetchFunction(`${posApiEndpoint}/clients/new?brand=${selectedBrand}`),
    enabled: clientDoesNotExistInPOS,
  });

  useSetFormFieldsState(formActions, editClientData);
  useSetFormFieldsState(formActions, createClientData);

  const { mutate: updateClient, isLoading: isLoadingPUT } = useMutation<
    ClientResponse,
    ErrorWithMessage,
    ClientPutRequestBody
  >({
    mutationFn: (body: ClientPutRequestBody) =>
      fetchFunction(`${posApiEndpoint}/clients/${body.id}`, {
        method: RequestMethod.PUT,
        body,
      }),
    onSuccess: (res: ClientResponse) =>
      dispatch(
        replacePOSAlerts({
          variant: AlertVariants.SUCCESS,
          heading: ALERTS.SUCCESS,
          text: `${res.data.name} Successfully Updated`,
          alertClearTimeout: 3000,
        })
      ),
  });

  const { mutate: createClient, isLoading: isLoadingPOST } = useMutation<
    ClientResponse,
    ErrorWithMessage,
    ClientPostRequestBody
  >({
    mutationFn: (body: ClientPostRequestBody) =>
      fetchFunction(`${posApiEndpoint}/clients`, {
        method: RequestMethod.POST,
        body,
      }),
    onSuccess: (res: ClientResponse) =>
      dispatch(
        replacePOSAlerts({
          variant: AlertVariants.SUCCESS,
          heading: ALERTS.SUCCESS,
          text: `${res.data.name} Successfully Created`,
          alertClearTimeout: 3000,
        })
      ),
  });

  useEffect(() => {
    if (clientExistsInPOS) {
      setFormType(FormType.EDIT);
    }
    if (clientDoesNotExistInPOS) {
      setFormType(FormType.CREATE);
    }
  }, [clientExistsInPOS, clientDoesNotExistInPOS]);

  if (editClientData || createClientData) {
    return (
      <FormContainer
        header={editClientData?.data.name || createClientData?.data.name || ""}
      >
        <FormInput
          disabled
          errorMessages={getErrorDictionary("Client Name")}
          fieldActions={formActions.fields.name}
          field={clientsForm.name}
          labelTextWhenNoError="Client Name"
        />
        <FormInput
          disabled
          errorMessages={getErrorDictionary("Brand")}
          fieldActions={formActions.fields.brand}
          field={clientsForm.brand}
          labelTextWhenNoError="Brand"
        />
        <FormInput
          disabled
          errorMessages={getErrorDictionary("Slug")}
          fieldActions={formActions.fields.slug}
          field={clientsForm.slug}
          labelTextWhenNoError="Slug"
        />
        <FormSelect
          fieldActions={formActions.fields.time_zone_id}
          errorMessages={getErrorDictionary("Timezone")}
          labelTextWhenNoError="Timezone"
          field={clientsForm.time_zone_id}
          options={getSelectOptionsWithPlaceholder(
            (editClientData?.data.time_zones || createClientData?.data.time_zones) ??
              [],
            clientsForm.time_zone_id.rawValue
          )}
        />
        <FormSelect
          fieldActions={formActions.fields.credit_card_reader_type_id}
          labelTextWhenNoError="Credit card reader type"
          errorMessages={getErrorDictionary("Credit card reader type")}
          field={clientsForm.credit_card_reader_type_id}
          options={getSelectOptionsWithPlaceholder(
            (editClientData?.data.credit_card_reader_types ||
              createClientData?.data.credit_card_reader_types) ??
              [],
            clientsForm.credit_card_reader_type_id.rawValue
          )}
        />
        <FormInput
          errorMessages={getErrorDictionary("Split Fee Text")}
          fieldActions={formActions.fields.split_fee_text_default}
          field={clientsForm.split_fee_text_default}
          labelTextWhenNoError="Split Fee Text"
          disabled={formType === FormType.CREATE}
        />
        <FormInput
          errorMessages={getErrorDictionary("Split Fee MID")}
          fieldActions={formActions.fields.split_fee_mid_default}
          field={clientsForm.split_fee_mid_default}
          labelTextWhenNoError="Split Fee MID"
          disabled={formType === FormType.CREATE}
        />
        <Alert
          text="Split fee mid & text are optional fields. If you intend to configure them ensure that both fields are provided & either the client or associated terminals have DC direct enabled."
          extraStyles="margin-top: 16px; margin-bottom: 16px;"
          showQuitLink={false}
        />
        <Cluster justify="flex-end">
          <ButtonWithAction
            disabled={Object.values(clientsForm).some(
              (formValue) => formValue.hasErrors
            )}
            extraStyles="margin: 0;"
            text={formType === FormType.CREATE ? "Create Client" : "Save Changes"}
            action={(e) => {
              e.preventDefault();
              if (
                clientsForm.credit_card_reader_type_id.rawValue &&
                clientsForm.time_zone_id.rawValue
              ) {
                if (formType === FormType.EDIT && editClientData) {
                  updateClient({
                    id: editClientData.data.id,
                    credit_card_reader_type_id:
                      clientsForm.credit_card_reader_type_id.rawValue,
                    time_zone_id: clientsForm.time_zone_id.rawValue,
                    split_fee_text_default:
                      clientsForm.split_fee_text_default.rawValue,
                    split_fee_mid_default:
                      clientsForm.split_fee_mid_default.rawValue,
                  });
                }
                if (formType === FormType.CREATE && createClientData) {
                  createClient({
                    name: createClientData.data.name,
                    brand: createClientData.data.brand,
                    slug: createClientData.data.slug,
                    credit_card_reader_type_id:
                      clientsForm.credit_card_reader_type_id.rawValue,
                    time_zone_id: clientsForm.time_zone_id.rawValue,
                  });
                }
              }
            }}
          />
        </Cluster>
      </FormContainer>
    );
  }

  if (
    isFetchingGET ||
    isFetchingEdit ||
    isFetchingCreate ||
    isLoadingPUT ||
    isLoadingPOST
  ) {
    return <Loading />;
  }

  return null;
};

ClientsForm.reducer = clientsFormReducer;
ClientsForm.mapStateToProps = clientsFormMapStateToProps;
ClientsForm.mapDispatchToProps = clientsFormMapDispatchToProps;

export default ClientsForm;
